import React, { useMemo, useState } from 'react';
import {
  ColumnPicker, DataTable, DateRangePicker, DensityToggle, EmptyState, exportFilename, exportRows,
  FilterBar, formatThaiDate, Pagination, SearchInput, SideDrawer, SourceBadge, StatusBadge,
  Toolbar, useTableDensity, useToast, AuditTrail,
  type ActiveFilter, type Column, type DateRange, type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  AUTO_FAILURE_LABEL, CLAIMS, PLATFORM_ORDER_URL, auditFor, pointsFor,
  type AutoCheckFailure, type Channel, type Claim,
} from './mockData';

/**
 * OrderIdClaimScreen — คำขอสะสมคะแนนจากเลขคำสั่งซื้อ (FR-ADM-11)
 *
 * หน้านี้ออกแบบเผื่อสองสถานะของการเชื่อม API ตั้งแต่แรก:
 *
 *   ยังไม่เชื่อม API (`connected: false`)
 *     ทุกคำขอเข้าคิวคน เจ้าหน้าที่เปิดหน้าแพลตฟอร์มตรวจเอง
 *     หน้าจอจึงเน้นปุ่มคัดลอกเลขคำสั่งซื้อและลิงก์เปิดแท็บใหม่
 *
 *   เชื่อม API แล้ว (`connected: true`)
 *     คำขอจากช่องทางที่เชื่อมสำเร็จ "ไม่ผ่านคิวคนอีกเลย"
 *     หน้านี้กลายเป็นหน้าติดตามผล + คิวเฉพาะรายการที่ระบบตรวจไม่ผ่าน
 *
 * สลับสองสถานะได้ที่หัวหน้าจอเพื่อให้ทีมเห็นทั้งสองแบบก่อนตัดสินใจ
 * — ในระบบจริงค่านี้มาจากสถานะการเชื่อมต่อของแต่ละช่องทาง ไม่ใช่ปุ่มให้ผู้ใช้กด
 */

type Mode = 'manual-queue' | 'auto-monitor';
type ScreenState = 'ready' | 'loading' | 'error';

const API_CHANNELS: Channel[] = ['Shopee', 'Lazada', 'TikTok Shop'];

/** สถานะการเชื่อมต่อรายช่องทาง — ในระบบจริงมาจากหน้าสถานะระบบ (FR-ADM-31…35) */
const CONNECTION: Array<{ channel: Channel; connected: boolean; lastSyncAt: string; failures24h: number }> = [
  { channel: 'Shopee', connected: true, lastSyncAt: '2026-08-21T09:12:00', failures24h: 0 },
  { channel: 'Lazada', connected: true, lastSyncAt: '2026-08-21T03:41:00', failures24h: 12 },
  { channel: 'TikTok Shop', connected: false, lastSyncAt: '—', failures24h: 0 },
];

const FAILURE_ACTION: Record<AutoCheckFailure, string> = {
  'order-not-found': 'ตรวจเลขคำสั่งซื้อกับลูกค้าอีกครั้ง หรือให้ลูกค้าส่งสลิปแทน',
  unpaid: 'รอให้ลูกค้าชำระเงิน แล้วให้ส่งคำขอใหม่',
  'already-claimed': 'ปฏิเสธได้ทันที — เลขนี้ถูกใช้ไปแล้ว',
  'api-timeout': 'ระบบจะลองใหม่อัตโนมัติ ไม่ต้องทำอะไร — ห้ามปฏิเสธเพราะ API ล่ม',
};

export interface OrderIdClaimScreenProps {
  initialState?: ScreenState;
  /** เริ่มที่มุมมองไหน — ค่าเริ่มต้นคือคิวรายการที่ระบบตรวจไม่ผ่าน */
  initialMode?: Mode;
}

export const OrderIdClaimScreen: React.FC<OrderIdClaimScreenProps> = ({
  initialState = 'ready',
  initialMode = 'manual-queue',
}) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [mode, setMode] = useState<Mode>(initialMode);
  const [density, setDensity] = useTableDensity();
  const [query, setQuery] = useState('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [failure, setFailure] = useState<'all' | AutoCheckFailure>('all');
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [sort, setSort] = useState<SortState | null>({ columnId: 'submittedAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [detail, setDetail] = useState<Claim | null>(null);

  const orderClaims = useMemo(() => CLAIMS.filter((c) => c.kind === 'order-id'), []);

  const base = useMemo(
    () => (mode === 'manual-queue'
      ? orderClaims.filter((c) => c.source === 'failed' || c.source === 'manual')
      : orderClaims.filter((c) => c.source === 'auto')),
    [orderClaims, mode],
  );

  const filtered = useMemo(() => base.filter((c) => {
    if (channels.length && !channels.includes(c.channel)) return false;
    if (failure !== 'all' && c.autoCheckFailure !== failure) return false;
    if (range.from && c.submittedAt.slice(0, 10) < range.from) return false;
    if (range.to && c.submittedAt.slice(0, 10) > range.to) return false;
    if (query) {
      const q = query.trim();
      if (!c.code.includes(q) && !(c.orderNumber ?? '').includes(q) && !c.member.name.includes(q)) return false;
    }
    return true;
  }), [base, channels, failure, range, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (c: Claim): string | number =>
      sort.columnId === 'amount' ? c.declaredAmount : sort.columnId === 'submittedAt' ? c.submittedAt : String(c[sort.columnId as keyof Claim] ?? '');
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const columns: Array<Column<Claim>> = useMemo(() => [
    { id: 'code', header: 'รหัสคำขอ', width: 122, pinned: true, required: true, sortable: true, value: (c) => c.code },
    {
      id: 'orderNumber', header: 'เลขคำสั่งซื้อ', width: 210, pinned: true, value: (c) => c.orderNumber ?? '',
      cell: (c) => (
        <span className="flex items-center gap-[var(--space-1)]">
          <span className="bo-num bo-truncate" style={{ textAlign: 'left' }}>{c.orderNumber}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(c.orderNumber ?? ''); toast.show({ tone: 'info', message: `คัดลอก ${c.orderNumber} แล้ว`, duration: 2000 }); }}
            aria-label={`คัดลอกเลขคำสั่งซื้อ ${c.orderNumber}`}
            className="inline-flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[var(--radius-xs)] text-[var(--ink-600)] hover:bg-[var(--blue-050)] hover:text-[var(--blue-700)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            <Icon name="content_copy" size={14} />
          </button>
          {PLATFORM_ORDER_URL[c.channel] && (
            <a
              href={`${PLATFORM_ORDER_URL[c.channel]}${c.orderNumber}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label={`เปิดคำสั่งซื้อ ${c.orderNumber} ใน ${c.channel}`}
              className="inline-flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[var(--radius-xs)] text-[var(--blue-700)] no-underline hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              <Icon name="open_in_new" size={14} />
            </a>
          )}
        </span>
      ),
    },
    { id: 'channel', header: 'ช่องทาง', width: 116, sortable: true, value: (c) => c.channel },
    { id: 'member', header: 'สมาชิก', width: 168, value: (c) => c.member.name },
    { id: 'amount', header: 'ยอด (บาท)', width: 118, numeric: true, sortable: true, value: (c) => c.declaredAmount, cell: (c) => c.declaredAmount.toLocaleString('th-TH') },
    { id: 'points', header: 'คะแนน', width: 96, numeric: true, value: (c) => pointsFor(c.declaredAmount), cell: (c) => pointsFor(c.declaredAmount).toLocaleString('th-TH') },
    { id: 'status', header: 'สถานะ', width: 150, sortable: true, value: (c) => c.status, cell: (c) => <StatusBadge status={c.status} /> },
    { id: 'source', header: 'การตรวจ', width: 180, value: (c) => c.source, cell: (c) => <SourceBadge source={c.source} /> },
    {
      id: 'failure', header: 'เหตุผลที่ระบบตรวจไม่ผ่าน', width: 300,
      value: (c) => (c.autoCheckFailure ? AUTO_FAILURE_LABEL[c.autoCheckFailure] : ''),
      cell: (c) =>
        c.autoCheckFailure ? (
          <span className="flex items-center gap-1 text-[var(--warning-text)]">
            <Icon name="sync_problem" size={14} filled />
            <span className="bo-truncate">{AUTO_FAILURE_LABEL[c.autoCheckFailure]}</span>
          </span>
        ) : <span className="text-[var(--ink-400)]">—</span>,
    },
    { id: 'submittedAt', header: 'วันที่ส่ง', width: 118, sortable: true, value: (c) => formatThaiDate(c.submittedAt.slice(0, 10)) },
  ], [toast]);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (channels.length) out.push({ id: 'channel', field: 'ช่องทาง', display: channels.join(', ') });
    if (failure !== 'all') out.push({ id: 'failure', field: 'เหตุผลที่ตรวจไม่ผ่าน', display: AUTO_FAILURE_LABEL[failure] });
    if (range.from || range.to) out.push({ id: 'range', field: 'ช่วงวันที่', display: `${formatThaiDate(range.from)} – ${formatThaiDate(range.to)}` });
    return out;
  }, [channels, failure, range]);

  const clearAll = () => { setChannels([]); setFailure('all'); setRange({ from: null, to: null }); setQuery(''); setPage(1); };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible, filename: exportFilename('คำขอจากเลขคำสั่งซื้อ'), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="คำขอจากเลขคำสั่งซื้อ"
        count={sorted.length}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาเลขคำสั่งซื้อ รหัสคำขอ หรือชื่อสมาชิก" width={340} />}
        end={
          <>
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="orderid-queue" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                CSV
              </button>
            </div>
          </>
        }
      />

      {/* สถานะการเชื่อมต่อรายช่องทาง — บอกว่าช่องทางไหนยังต้องใช้คนตรวจ */}
      <section aria-label="สถานะการเชื่อมต่อ API" className="flex flex-wrap items-center gap-[var(--space-3)] border-b border-[var(--line-200)] bg-[var(--blue-050)] px-[var(--space-4)] py-[var(--space-2)]">
        {CONNECTION.map((c) => (
          <div key={c.channel} className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--line-200)] bg-[var(--surface-0)] px-[var(--space-25)] py-[var(--space-1)]">
            <Icon
              name={c.connected ? (c.failures24h > 0 ? 'warning' : 'cloud_done') : 'cloud_off'}
              size={18}
              filled
              className={c.connected ? (c.failures24h > 0 ? 'text-[var(--warning-text)]' : 'text-[var(--success-text)]') : 'text-[var(--ink-600)]'}
            />
            <div>
              <p className="text-[length:var(--text-form)] font-[var(--weight-medium)] leading-[var(--leading-form)] text-[var(--ink-900)]">{c.channel}</p>
              <p className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">
                {c.connected
                  ? `เชื่อมแล้ว · ซิงก์ล่าสุด ${formatThaiDate(c.lastSyncAt.slice(0, 10))}${c.failures24h ? ` · ล้มเหลว ${c.failures24h} ครั้งใน 24 ชม.` : ''}`
                  : 'ยังไม่เชื่อม — คำขอช่องทางนี้ต้องใช้คนตรวจทุกใบ'}
              </p>
            </div>
          </div>
        ))}
        <p className="ml-auto max-w-[42ch] text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
          เมื่อช่องทางใดเชื่อมสำเร็จ คำขอจากช่องทางนั้นจะไม่ผ่านคิวคนอีก เหลือเฉพาะรายการที่ระบบตรวจไม่ผ่าน
        </p>
      </section>

      {/* สองมุมมอง: คิวที่ต้องใช้คน กับ รายการที่ระบบตรวจเอง */}
      <div role="tablist" aria-label="มุมมอง" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        {([['manual-queue', 'ต้องใช้คนตรวจ', base.length], ['auto-monitor', 'ระบบตรวจอัตโนมัติ', orderClaims.filter((c) => c.source === 'auto').length]] as Array<[Mode, string, number]>).map(([id, label]) => {
          const on = mode === id;
          const count = id === 'manual-queue'
            ? orderClaims.filter((c) => c.source === 'failed' || c.source === 'manual').length
            : orderClaims.filter((c) => c.source === 'auto').length;
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => { setMode(id); setPage(1); }}
              className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)]'
              }`}
            >
              <Icon name={id === 'manual-queue' ? 'draw' : 'bolt'} size={18} filled={on} />
              {label}
              <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${on ? 'bg-[var(--blue-700)] text-white' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'}`}>
                {count.toLocaleString('th-TH')}
              </span>
            </button>
          );
        })}
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={(id) => { if (id === 'channel') setChannels([]); if (id === 'failure') setFailure('all'); if (id === 'range') setRange({ from: null, to: null }); setPage(1); }}
        onClearAll={clearAll}
        currentFilters={{ mode, channels, failure, range }}
        persistKey="orderid-queue"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ช่องทาง
          <select
            multiple
            size={1}
            value={channels}
            onChange={(e) => { setChannels([...e.target.selectedOptions].map((o) => o.value as Channel)); setPage(1); }}
            className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
          >
            {API_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        {mode === 'manual-queue' && (
          <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
            เหตุผลที่ตรวจไม่ผ่าน
            <select
              value={failure}
              onChange={(e) => { setFailure(e.target.value as typeof failure); setPage(1); }}
              className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              <option value="all">ทั้งหมด</option>
              {(Object.keys(AUTO_FAILURE_LABEL) as AutoCheckFailure[]).map((k) => <option key={k} value={k}>{AUTO_FAILURE_LABEL[k]}</option>)}
            </select>
          </label>
        )}

        <DateRangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} />
      </FilterBar>

      <DataTable<Claim>
        columns={columns.filter((c) => visible.includes(c.id))}
        rows={rows}
        rowKey={(c) => c.id}
        caption={mode === 'manual-queue' ? 'คำขอจากเลขคำสั่งซื้อที่ต้องใช้คนตรวจ' : 'คำขอจากเลขคำสั่งซื้อที่ระบบตรวจอัตโนมัติ'}
        state={screenState}
        rowCountHint={Math.min(pageSize, 14)}
        errorMessage="โหลดรายการไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
        onRetry={() => setScreenState('ready')}
        emptyState={
          activeFilters.length || query ? (
            <EmptyState kind="no-results" title="ไม่พบรายการที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
          ) : mode === 'manual-queue' ? (
            <EmptyState kind="queue-clear" title="ไม่มีคำขอที่ต้องใช้คนตรวจ" description="ระบบตรวจคำขอจากเลขคำสั่งซื้อได้เองทั้งหมดในช่วงนี้" />
          ) : (
            <EmptyState kind="no-data" title="ยังไม่มีรายการที่ระบบตรวจอัตโนมัติ" description="เริ่มมีข้อมูลเมื่อเชื่อม API ของแพลตฟอร์มสำเร็จ" />
          )
        }
        sort={sort}
        onSortChange={setSort}
        onOpenRow={setDetail}
        density={density}
        persistKey="orderid-queue"
        className="min-h-0 flex-1 rounded-none border-0"
      />

      <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />

      <SideDrawer open={Boolean(detail)} onClose={() => setDetail(null)} title="รายละเอียดคำขอ" subtitle={detail?.code} width="lg">
        {detail && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <div className="flex flex-wrap items-center gap-[var(--space-2)]">
              <StatusBadge status={detail.status} size="md" />
              <SourceBadge source={detail.source} />
            </div>

            <dl className="grid grid-cols-2 gap-[var(--space-3)] text-[length:var(--text-form-lg)] leading-[var(--leading-form)]">
              <div><dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">เลขคำสั่งซื้อ</dt><dd className="bo-num font-[var(--weight-medium)]" style={{ textAlign: 'left' }}>{detail.orderNumber}</dd></div>
              <div><dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ช่องทาง</dt><dd className="font-[var(--weight-medium)]">{detail.channel}</dd></div>
              <div><dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">สมาชิก</dt><dd className="font-[var(--weight-medium)]">{detail.member.name}</dd></div>
              <div><dt className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">ยอด / คะแนน</dt><dd className="bo-num font-[var(--weight-medium)]" style={{ textAlign: 'left' }}>{detail.declaredAmount.toLocaleString('th-TH')} บาท · {pointsFor(detail.declaredAmount)} คะแนน</dd></div>
            </dl>

            {detail.autoCheckFailure && (
              <div role="alert" className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)]">
                <p className="flex items-center gap-[var(--space-1)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-[var(--warning-text)]">
                  <Icon name="sync_problem" size={18} filled />
                  {AUTO_FAILURE_LABEL[detail.autoCheckFailure]}
                </p>
                <p className="text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                  สิ่งที่ควรทำต่อ: {FAILURE_ACTION[detail.autoCheckFailure]}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-[var(--space-2)]">
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(detail.orderNumber ?? '')}
                className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-900)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                <Icon name="content_copy" size={16} />คัดลอกเลขคำสั่งซื้อ
              </button>
              {PLATFORM_ORDER_URL[detail.channel] && (
                <a
                  href={`${PLATFORM_ORDER_URL[detail.channel]}${detail.orderNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--blue-700)] no-underline hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
                >
                  <Icon name="open_in_new" size={16} />เปิดใน {detail.channel}
                </a>
              )}
              <a
                href={`/queue/claims?claim=${detail.code}`}
                className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-white no-underline hover:bg-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
              >
                <Icon name="gavel" size={16} />เปิดในคิวเพื่อตัดสิน
              </a>
            </div>

            <section>
              <h3 className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">ประวัติการกระทำ</h3>
              <AuditTrail entries={auditFor(detail) as never} />
            </section>
          </div>
        )}
      </SideDrawer>
    </div>
  );
};

export default OrderIdClaimScreen;

/* ============================================================================
   รองรับ:
   FR-ADM-11  คิวคำขอจากเลขคำสั่งซื้อ — สองมุมมอง (ต้องใช้คนตรวจ / ระบบตรวจอัตโนมัติ)
              พร้อมปุ่มคัดลอกเลขและลิงก์เปิดหน้าแพลตฟอร์มสำหรับช่วงที่ยังไม่เชื่อม API
   FR-ADM-14  แสดงสถานะการเชื่อมต่อและจำนวนครั้งที่ API ล้มเหลวใน 24 ชม.
   FR-ADM-31…35  สถานะการเชื่อมต่อรายช่องทางเป็นข้อมูลเดียวกับหน้าสถานะระบบ
   FR-ADM-37  ประวัติการกระทำของแต่ละคำขอแสดงใน SideDrawer
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้
   หมายเหตุนโยบาย: กรณี api-timeout ห้ามปฏิเสธคำขอ ต้องให้ระบบลองใหม่
   (PROGRESS-REPORT 3.1 — ห้ามปฏิเสธผู้ใช้เพราะ API ล่ม)
   ============================================================================ */
